import { useState, useEffect, useCallback, useRef } from "react"

const SERIAL_NUMBER_LENGTH = 10

export enum ScannerConsumerType {
   HEADER_SWAP = "header-swap",
   NEW_FLOOR_OVERVIEW = "new-floor-overview",
   SETTINGS_WEARABLES = "settings-wearables",
   DEFAULT = "default",
}

const SCANNER_CONSUMER_PRECEDENCE: ScannerConsumerType[] = [
   ScannerConsumerType.HEADER_SWAP,
   ScannerConsumerType.NEW_FLOOR_OVERVIEW,
   ScannerConsumerType.SETTINGS_WEARABLES,
   ScannerConsumerType.DEFAULT,
]

interface ScannerSubscriber {
   id: string
   consumerType: ScannerConsumerType
   enabled: boolean
   onScan: (value: string) => void
}

interface UseScannerOptions {
   consumerId?: string
   consumerType?: ScannerConsumerType
   enabled?: boolean
}

let scannerBuffer: string[] = []
let scannerListenerAttached = false
let scannerSubscriberCounter = 0
const scannerSubscribers = new Map<string, ScannerSubscriber>()

const getSubscriberPrecedenceIndex = (consumerType: ScannerConsumerType) => {
   const index = SCANNER_CONSUMER_PRECEDENCE.indexOf(consumerType)
   return index === -1 ? SCANNER_CONSUMER_PRECEDENCE.length : index
}

const getActiveSubscriberByMapping = () => {
   const enabledSubscribers = Array.from(scannerSubscribers.values()).filter(
      (subscriber) => subscriber.enabled
   )

   if (enabledSubscribers.length === 0) return null

   return enabledSubscribers.reduce((winner, current) =>
      getSubscriberPrecedenceIndex(current.consumerType) <
      getSubscriberPrecedenceIndex(winner.consumerType)
         ? current
         : winner
   )
}

const dispatchScan = (value: string) => {
   const targetSubscriber = getActiveSubscriberByMapping()
   if (!targetSubscriber) return
   targetSubscriber.onScan(value)
}

const handleScannerKeyDown = (e: KeyboardEvent) => {
   if (e.key === "Enter" && scannerBuffer.length === SERIAL_NUMBER_LENGTH) {
      e.preventDefault()
      dispatchScan(scannerBuffer.join(""))
      scannerBuffer = []
      return
   }

   if (/\d/.test(e.key)) {
      if (scannerBuffer.length === SERIAL_NUMBER_LENGTH) scannerBuffer.shift()
      scannerBuffer.push(e.key)
   }
}

const syncScannerListener = () => {
   if (!scannerListenerAttached && scannerSubscribers.size > 0) {
      window.addEventListener("keydown", handleScannerKeyDown)
      scannerListenerAttached = true
      return
   }

   if (scannerListenerAttached && scannerSubscribers.size === 0) {
      window.removeEventListener("keydown", handleScannerKeyDown)
      scannerListenerAttached = false
      scannerBuffer = []
   }
}

const registerScannerSubscriber = (subscriber: ScannerSubscriber) => {
   scannerSubscribers.set(subscriber.id, subscriber)
   syncScannerListener()
}

const updateScannerSubscriber = (
   id: string,
   updates: Pick<ScannerSubscriber, "consumerType" | "enabled" | "onScan">
) => {
   const existingSubscriber = scannerSubscribers.get(id)
   if (!existingSubscriber) return

   scannerSubscribers.set(id, {
      ...existingSubscriber,
      ...updates,
   })
}

const unregisterScannerSubscriber = (id: string) => {
   scannerSubscribers.delete(id)
   syncScannerListener()
}

export const useScanner = ({
   consumerId,
   consumerType = ScannerConsumerType.DEFAULT,
   enabled = true,
}: UseScannerOptions = {}) => {
   const [scannedId, setScannedId] = useState<string | null>(null)
   const subscriberIdRef = useRef<string | null>(null)

   if (!subscriberIdRef.current) {
      subscriberIdRef.current =
         consumerId ?? `scanner-consumer-${scannerSubscriberCounter++}`
   }

   const resetScannedId = useCallback(() => setScannedId(null), [])
   const onScan = useCallback((value: string) => setScannedId(value), [])

   useEffect(() => {
      registerScannerSubscriber({
         id: subscriberIdRef.current as string,
         consumerType,
         enabled,
         onScan,
      })

      return () => {
         unregisterScannerSubscriber(subscriberIdRef.current as string)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only subscribe/unsubscribe; consumerType/enabled/onScan changes are handled by the update effect below
   }, [])

   useEffect(() => {
      updateScannerSubscriber(subscriberIdRef.current as string, {
         consumerType,
         enabled,
         onScan,
      })
   }, [consumerType, enabled, onScan])

   return { scannedId, resetScannedId }
}
